import { ref, computed } from 'vue';
import { ValidExperimentName, ExperimentStatus } from '../types';

type ExperimentActivePlacements = Partial<Record<ValidExperimentName, Set<string>>>;

export const experimentActivePlacements = ref<ExperimentActivePlacements>({});

let index = 0;
function getExperimentPlacementId(experimentName: ValidExperimentName): string {
	return `${experimentName}-${index++}`;
}
/**
 * useExperimentStatus manages the status of an experiment.
 * It allows activating/deactivating the experiment in multiple placements and provides its current status.
 * This ensures independent control of placements, even in components rendered multiple times on the same page.
 * (Placement refers to the component where useActiveExperiment was called)
 * @param experimentName The name of the experiment.
 */
export function useExperimentStatus(experimentName: ValidExperimentName) {
	const placementId = getExperimentPlacementId(experimentName);

	function activateExperimentPlacement() {
		const current = experimentActivePlacements.value[experimentName] ?? new Set<string>();
		if (current.has(placementId)) return;

		experimentActivePlacements.value = {
			...experimentActivePlacements.value,
			[experimentName]: current.add(placementId),
		};
	}

	function deactivateExperimentPlacement() {
		const currentExperiment = experimentActivePlacements.value[experimentName];
		if (!currentExperiment || !currentExperiment.has(placementId)) {
			return;
		}

		currentExperiment.delete(placementId);

		experimentActivePlacements.value = {
			...experimentActivePlacements.value,
			[experimentName]: currentExperiment,
		};
	}

	const status = computed<ExperimentStatus>(() => {
		return getGlobalExperimentStatus(experimentName, experimentActivePlacements.value);
	});

	const localPlacementStatus = computed<ExperimentStatus>(() => {
		return experimentActivePlacements.value?.[experimentName]?.has(placementId) ? 'ACTIVE' : 'ACQUIRED';
	});

	return {
		/**
		 * This allows for independent activate the experiment in different placements.
		 */
		activateExperimentPlacement,
		/**
		 * This allows for independent deactivate the experiment in different placements.
		 */
		deactivateExperimentPlacement,
		/**
		 * The current global status of the experiment 'ACTIVE' if at least one placement was activated.
		 */
		status,
		/**
		 * The current status of the experiment for the specific placement.
		 */
		localPlacementStatus,
	};
}

export function getGlobalExperimentStatus(
	experimentName: ValidExperimentName,
	experimentActivePlacementsMap: ExperimentActivePlacements
): ExperimentStatus {
	const activePlacementsCount = experimentActivePlacementsMap?.[experimentName]?.size;
	if (!!activePlacementsCount) return 'ACTIVE';
	return 'ACQUIRED';
}
